import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useUserAuthStore } from 'src/stores/user/auth'
import {
  REGION_CURRENCIES,
  getSumRegionByRegion,
  isEuropeFeatureEnabled,
  isEuropeRegion,
  normalizeSumRegionRules,
  normalizeRegionCode,
} from 'src/common/utils/regions'

export const useSiteInfoStore = defineStore('siteInfo', () => {
  const siteInfo = ref(null)
  const sumRegionRules = ref(null)
  const currencyList = ref(null)
  const defaultCurrency = ref({
    code: 'KZT',
    name: 'Kazakhstan tenge',
    objId: 15,
  })
  const seoData = ref(null)

  const getCountryCode = computed(() => {
    return useUserAuthStore().getUserRegion ? useUserAuthStore().getUserRegion : siteInfo.value?.region
  })
  const userCurrency = computed(() => {
    return useUserAuthStore().user?.currency
  })

  const getCurrency = computed(() => {
    return useUserAuthStore().getUserCurrency ? useUserAuthStore().getUserCurrency : defaultCurrency.value
  })
  const getCurrencyCode = computed(() => {
    return getCurrency.value.code
  })

  const getMainDomain = computed(() => {
    return siteInfo.value?.mainDomain ?? null
  })
  const autoAcceptRules = computed(() => {
    return siteInfo.value?.autoAcceptRules ?? false
  })

  const isWebView = ref(process.env.WEBVIEW === '1')
  const getCurrentDomain = computed(() => {
    return siteInfo.value?.domain ?? null
  })
  const getMetaTitle = computed(() => {
    return seoData.value?.metaTitle ?? null
  })

  const getMetaDescription = computed(() => {
    return seoData.value?.metaDescription ?? null
  })
  const getSeoDescription = computed(() => {
    return seoData.value?.seoText ?? null
  })
  const getUserIp = computed(() => {
    return siteInfo.value?.realIp ?? null
  })
  const getRegion = computed(() => {
    return siteInfo.value?.region ?? null
  })
  const getSumRegion = computed(() => {
    if (!isEuropeFeatureEnabled()) {
      return null
    }

    const userSumRegion = useUserAuthStore().getUserSumRegion
    if (userSumRegion) {
      return userSumRegion
    }

    return getSumRegionByRegion(sumRegionRules.value, siteInfo.value?.region)
  })
  const isEuropeGeo = computed(() => {
    return isEuropeFeatureEnabled() && isEuropeRegion(getSumRegion.value)
  })
  const isEuropeRegionByRegion = (region) => {
    if (!isEuropeFeatureEnabled()) {
      return false
    }

    return isEuropeRegion(getSumRegionByRegion(sumRegionRules.value, region))
  }
  const getRealRegion = computed(() => {
    return siteInfo.value?.realRegion ?? null
  })
  const getAvailableRegions = computed(() => {
    return siteInfo.value?.availableRegions ?? []
  })
  const getIpCountryRegion = computed(() => {
    const realRegion = normalizeRegionCode(siteInfo.value?.realRegion)
    if (realRegion) {
      return realRegion
    }

    const region = normalizeRegionCode(siteInfo.value?.region)

    return isEuropeRegion(region) ? null : region || null
  })
  const getEuropeRegistrationRegionByCurrency = (currencyCode) => {
    if (!isEuropeGeo.value) {
      return null
    }

    const normalizedCurrencyCode = normalizeRegionCode(currencyCode)

    if (normalizedCurrencyCode === 'EUR') {
      return getIpCountryRegion.value
    }

    if (normalizedCurrencyCode === 'USD') {
      return null
    }

    return Object.entries(REGION_CURRENCIES).find(([, currency]) => currency === normalizedCurrencyCode)?.[0] ?? null
  }
  const getShowDocNum = computed(() => {
    return siteInfo.value?.showDocNum ?? null
  })
  const getFullCheck = computed(() => {
    return siteInfo.value?.fullCheck
  })
  const getMobileAppUrl = computed(() => {
    return siteInfo.value?.mobileAppUrl
  })
  const getMobileAppUrlIos = computed(() => {
    return siteInfo.value?.mobileAppUrlIos
  })
  const getMobileAppUrlAndroid = computed(() => {
    return siteInfo.value?.mobileAppUrlAndroid
  })

  function updateMobileAppUrlIos(url) {
    siteInfo.value.mobileAppUrlIos = url
  }

  function updateSiteInfo(payload) {
    siteInfo.value = payload
    if (payload?.isWebview) {
      document.querySelector('body').classList.add('web-view')
    }
  }

  function updateSumRegionRules(payload) {
    sumRegionRules.value = isEuropeFeatureEnabled() ? normalizeSumRegionRules(payload) : null
  }

  function updateSeoData(payload) {
    seoData.value = payload
  }

  function updateFullCheck(payload) {
    if (siteInfo.value) {
      siteInfo.value = { ...siteInfo.value, fullCheck: payload }
    }
  }

  function setCurrencyData(payload) {
    currencyList.value = payload.currencies
    defaultCurrency.value = payload.defaultCurrency
  }

  function findCurrencyCodeById(payload) {
    return currencyList.value?.find((currency) => currency.objId === payload)?.code || defaultCurrency.value?.code
  }

  return {
    // refs
    siteInfo,
    sumRegionRules,
    currencyList,
    defaultCurrency,
    seoData,
    getMobileAppUrl,
    getMobileAppUrlIos,
    getMobileAppUrlAndroid,
    // computed
    getCountryCode,
    getCurrency,
    getCurrencyCode,
    getMainDomain,
    autoAcceptRules,
    isWebView,
    getCurrentDomain,
    getMetaTitle,
    getMetaDescription,
    getSeoDescription,
    getUserIp,
    getRegion,
    getSumRegion,
    isEuropeGeo,
    isEuropeRegionByRegion,
    getRealRegion,
    getAvailableRegions,
    getIpCountryRegion,
    getEuropeRegistrationRegionByCurrency,
    getShowDocNum,
    getFullCheck,
    userCurrency,
    //functions
    updateSiteInfo,
    updateSumRegionRules,
    updateSeoData,
    setCurrencyData,
    updateMobileAppUrlIos,
    updateFullCheck,
    findCurrencyCodeById,
  }
})
